import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { 
    selectUser, 
    updateChatHistoryEntry, 
    selectVideoProgress, 
    selectVideoTimerActive, 
    startVideoProgressTimer, 
    resetVideoTimer, 
    selectVideoTimerCompleted,
    selectIsLoading,
    selectTilesDetails,
    selectRestrictedDataError,
    fetchData,
    resetState,
    setChatbotId,
    selectOpenAiTokens,
    setOpenAiTokens,
    fetchUserData,
    selectActiveSubscription,
    selectGenerationCounts,
    fetchActiveSubscription
} from '../../../../mainApp/features/DataSlice/dataSlice';
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotInput,
  ChatbotError,
  AppTitle,
  LoadingElement,
  VideoWrapper,
  VideoDisplay,
  ChatbotSelect,
  ArtistImage,
  ChatbotExpandButton,
  GenerationCounter
} from "./styled";
import {LoadingSpinner, ProgressContainer} from '../MusicCreator/styled';
import { 
    ChatbotButton, 
    ChatbotMessageDescription,
} from "../styled";
import LoadingPage from '../../TechnicalTabs/LoadingPage';
import ErrorPage from '../../../../mainApp/features/TechnicalTabs/ErrorPage';
import { handleUnauthorizedAccess } from '../../Utility/handleUnauthorizedAccess';
import styled from 'styled-components';

const InlineLinkButton = styled.a`
  background: linear-gradient(135deg, #1db954, #179544);
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  display: inline-block;
  padding: 2px 8px;
  font-size: 13px;
  margin-left: 8px;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 10px rgba(0, 191, 255, 0.3);
  }
`;

const models = [
     { model: "Qubico/hunyuan", task_type: "txt2video", name: "Podstawowy" },
    { model: "Qubico/wanx", task_type: "txt2video-1.3b", name: "Standard" },
    { model: "Qubico/wanx", task_type: "txt2video-14b", name: "Jakość" }
   
];

const VIDEO_TOKEN_COSTS = {
    "Podstawowy": 50,
    "Standard": 70,
    "Jakość": 150,
};

const VideoCreatorChatbot = () => {
    const [prompt, setPrompt] = useState('');
    const [selectedModel, setSelectedModel] = useState(models[0]);
    const [aspectRatio, setAspectRatio] = useState('16:9');
    const [generationIsLoading, setGenerationIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unlockStatusMessage, setUnlockStatusMessage] = useState(""); // Nowy stan
    const [messages, setMessages] = useState([]);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [processedTaskIds, setProcessedTaskIds] = useState(new Set());
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(true);
    const timeoutRef = useRef(null);

    const user = useSelector(selectUser);
    const videoProgress = useSelector(selectVideoProgress);
    const videoTimerActive = useSelector(selectVideoTimerActive);
    const videoTimerCompleted = useSelector(selectVideoTimerCompleted);
    const isLoading = useSelector(selectIsLoading);
    const tilesDetails = useSelector(selectTilesDetails);
    const restrictedDataError = useSelector(selectRestrictedDataError);
    const currentTokens = useSelector(selectOpenAiTokens);
    const activeSubscription = useSelector(selectActiveSubscription);
    const generationCounts = useSelector(selectGenerationCounts);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const STANDARD_MONTHLY_ID = process.env.REACT_APP_STANDARD_MONTHLY;
    const STANDARD_YEARLY_ID = process.env.REACT_APP_STANDARD_YEARLY;

    const isStandardPlan = activeSubscription && 
        (activeSubscription.priceId === STANDARD_MONTHLY_ID || activeSubscription.priceId === STANDARD_YEARLY_ID);

    const videoGenerations = generationCounts?.video || 0;
    const remainingGenerations = 3 - videoGenerations;
    const limitToastId = 'video-creator-limit-toast';

    useEffect(() => {
        dispatch(setChatbotId('video'));
        dispatch(resetState());
        dispatch(fetchData());
        dispatch(fetchActiveSubscription());

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (!currentUser) {
                navigate('/login');
            }
        });

        return () => {
            dispatch(resetState());
            unsubscribe();
        };
    }, [dispatch, navigate]);

    useEffect(() => {
        if (isStandardPlan) {
          const toastOptions = {
            toastId: limitToastId,
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          };
    
          const message = `Limit generacji wideo w planie Standard: ${remainingGenerations}/3`;
    
          if (toast.isActive(limitToastId)) {
            toast.update(limitToastId, { render: message });
          } else {
            toast.info(message, toastOptions);
          }
        }
      }, [isStandardPlan, remainingGenerations]);

    useEffect(() => {
        if (restrictedDataError) {
            navigate('/unauthorized');
        }
    }, [restrictedDataError, navigate]);

    useEffect(() => {
        if (user && currentTaskId) {
            const unsub = onSnapshot(doc(db, `Users/${user.uid}/chatHistory`, currentTaskId), (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    const messageData = { id: doc.id, ...data };

                    setMessages(prevMessages => {
                        const existingMessageIndex = prevMessages.findIndex(m => m.id === doc.id);
                        if (existingMessageIndex > -1) {
                            const updatedMessages = [...prevMessages];
                            updatedMessages[existingMessageIndex] = messageData;
                            return updatedMessages;
                        } else {
                            return [...prevMessages, messageData];
                        }
                    });

                    if (data.status === 'processing' && !videoTimerActive && !videoTimerCompleted) {
                        // Start progresu tylko raz na cykl generowania
                        dispatch(startVideoProgressTimer());
                    }

                    if ((data.status === 'completed' || data.status === 'error') && !processedTaskIds.has(currentTaskId)) {
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                        }
                        setGenerationIsLoading(false);
                        if(data.status === 'completed') {
                            dispatch(updateChatHistoryEntry(messageData));
                            dispatch(fetchUserData()); // Refresh user data (including tokens) on success
                        } else if (data.status === 'error') {
                            setError(data.content || 'Wystąpił nieznany błąd podczas generowania wideo.');
                        }
                        // Zatrzymaj/zresetuj timer po zakończeniu lub błędzie
                        dispatch(resetVideoTimer());
                        setProcessedTaskIds(prev => new Set(prev).add(currentTaskId));
                    }
                }
            });
            return () => {
                unsub();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [user, currentTaskId, dispatch, processedTaskIds, videoTimerActive, videoTimerCompleted]);

    // Nowa funkcja do odblokowywania wideo
    async function handleUnlockVideo() {
        setUnlockStatusMessage("Próba odblokowania...");
        const user = auth.currentUser;
        if (!user) {
            setUnlockStatusMessage("Błąd: Użytkownik nie jest zalogowany.");
            return;
        }

        try {
            const token = await user.getIdToken();
            const response = await fetch('/.netlify/functions/force-unlock-video-generation', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.ok) {
                setUnlockStatusMessage("Sukces! Blokada została usunięta. Możesz teraz spróbować ponownie wygenerować wideo.");
                setError(null); // Wyczyść stary błąd
            } else {
                throw new Error(data.error || "Nieznany błąd.");
            }
        } catch (error) {
            console.error("Błąd podczas odblokowywania wideo:", error);
            setUnlockStatusMessage(`Błąd: ${error.message}`);
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!prompt.trim() || !user) return;

        if (isStandardPlan && remainingGenerations <= 0) {
            toast.error("Osiągnięto limit generacji wideo w planie Standard.", {
                position: "top-center",
            });
            return;
        }

        const cost = VIDEO_TOKEN_COSTS[selectedModel.name];
        if (currentTokens < cost) {
            toast.error("Masz za mało tokenów, aby wygenerować to wideo.", {
                position: "top-center",
            });
            return;
        }

        setGenerationIsLoading(true);
        setError(null);
        setUnlockStatusMessage(""); // Resetuj komunikat przy nowym generowaniu
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch('/.netlify/functions/start-video-generation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ prompt, model: selectedModel.model, task_type: selectedModel.task_type, aspectRatio: aspectRatio })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Coś poszło nie tak');
            }
            const data = await response.json();
            setCurrentTaskId(data.taskId);
            // Token update removed from here

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setError("Przeciążenie sieci, spróbuj ponownie za kilka minut, przepraszamy!");
                setGenerationIsLoading(false);
                dispatch(resetVideoTimer());
                setProcessedTaskIds(prev => new Set(prev).add(data.taskId));
            }, 14 * 60 * 1000);

            setMessages(prev => [...prev, { id: `user-${Date.now()}`, isUser: true, content: prompt, timestamp: new Date() }]);
            setPrompt('');
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
            setGenerationIsLoading(false);
        }
    };

    const renderContent = (content, msg) => {
        if (msg.status === 'completed' && content.startsWith('http') && (content.includes('storage.googleapis.com'))) {
            return <VideoWrapper><VideoDisplay src={content} controls /></VideoWrapper>;
        }
        return <p>{content}</p>;
    };

    const toggleDetailsPanel = () => setIsDetailsExpanded((p) => !p);
    
    const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;

    if (isLoading === 'loading') {
        return <LoadingPage />;
    }

    if (isLoading === 'error' || restrictedDataError) {
        return <ErrorPage />;
    }

    let returned;
    if (isLoading === 'success') {
        if (!user) {
            returned = <LoadingPage />;
        } else if (tilesDetails.length > 0 && user) {
            returned = (
                <ChatbotContainer isImage>
                    <ChatbotMain isDetailsExpanded={isDetailsExpanded} isImage isDetailsPanelVisible>
                        <AppTitle isImage>Generator Wideo</AppTitle>
                        <ChatbotForm onSubmit={handleSubmit} isImage>
                            <ChatbotInput
                                type="text"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Wpisz opis wideo, np. 'lecący smok nad zamkiem'..."
                                disabled={generationIsLoading}
                                isImage
                            />
                            <div style={{display: 'flex', gap: '10px'}}>
                                <ChatbotSelect value={JSON.stringify(selectedModel)} onChange={(e) => setSelectedModel(JSON.parse(e.target.value))} disabled={generationIsLoading}>
                                    {models.map(m => (<option key={m.name} value={JSON.stringify(m)}>{m.name}</option>))}
                                </ChatbotSelect>
                                <ChatbotSelect value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} disabled={generationIsLoading}>
                                    <option value="16:9">16:9 (Poziomy)</option>
                                    <option value="9:16">9:16 (Pionowy)</option>
                                </ChatbotSelect>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {isStandardPlan && (
                                    <GenerationCounter remaining={remainingGenerations}>
                                    {remainingGenerations}/3
                                    </GenerationCounter>
                                )}
                                <ChatbotButton 
                                    type="submit" 
                                    disabled={generationIsLoading || !prompt.trim()} 
                                    isImage
                                    tokenCost={VIDEO_TOKEN_COSTS[selectedModel.name]}
                                >
                                    {generationIsLoading ? 'Generowanie...' : 'Generuj Wideo'}
                                </ChatbotButton>
                            </div>
                        </ChatbotForm>
                        
                        {error ? (
                            <ChatbotError isImage>
                                {error}
                                {error.includes("Jedno wideo jest już w trakcie generowania") && (
                                    <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                        <p>Jeśli jesteś pewien, że komunikat pojawił się omyłkowo (to się zdarza w wersji BETA), możesz wymusić odblokowanie.</p>
                                        <ChatbotButton 
                                            onClick={handleUnlockVideo} 
                                            style={{ marginTop: '10px', fontSize: '14px', padding: '8px 12px' }}
                                        >
                                            Odblokuj ręcznie
                                        </ChatbotButton>
                                         <p style={{ marginTop: "7px" }}>UWAGA ⚠️ Sprawdź historię - wymuszenie w trakcie aktywnej generacji może spowodować błąd i utratę tokenów.</p>
                                        {unlockStatusMessage && <p style={{ marginTop: '10px', fontSize: '12px' }}>{unlockStatusMessage}</p>}
                                    </div>
                                )}
                            </ChatbotError>
                        )
                        : generationIsLoading ? (
                            <ProgressContainer>
                          <LoadingElement isImage>
                            Generowanie wideo, to może potrwać kilka minut...
                          </LoadingElement>
                          {typeof videoProgress === 'number' && (
                            <div style={{ color: '#fff', fontSize: '22px', fontWeight: 600, margin: '8px 0' }}>
                              {videoProgress}%
                              {videoProgress === 100 && videoTimerCompleted && <span style={{ marginLeft: '10px', fontSize: '16px', opacity: 0.8 }}>Jeszcze chwila...</span>}
                            </div>
                          )}
                          <LoadingSpinner/>
                          <div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.8 }}>
                            <span style={{  fontSize: '14px',  color: "#cd5c5c" }}>UWAGA!</span> <span style={{  fontSize: '14px',  color: "#E2E0DA" }}> Nie odświeżaj strony aż do zakończenia generowania.</span><div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.8, color:'#E2E0DA' }}> ☕ Zrelaksuj się: skomplikowane wideo mogą potrzebować do 12 minut.</div><div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.8, color:'#E2E0DA'}}>W trakcie możesz korzystać z innych aplikacji.<InlineLinkButton href="/home" target="_blank" rel="noopener noreferrer">Otwórz</InlineLinkButton></div>
                          </div>
                               </ProgressContainer>
                        ) 
                        : latestMessage && latestMessage.status === 'completed' ? (renderContent(latestMessage.content, latestMessage))
                        : latestMessage && latestMessage.status === 'error' ? (<ChatbotError isImage>{latestMessage.content}</ChatbotError>)
                        : messages.length > 0 && latestMessage.status === 'processing' ? (
                          <ProgressContainer>
                            {typeof videoProgress === 'number' && (
                              <div style={{ color: '#fff', fontSize: '22px', fontWeight: 600, margin: '8px 0' }}>
                                {videoProgress}%
                                {videoProgress === 100 && videoTimerCompleted && <span style={{ marginLeft: '10px', fontSize: '16px', opacity: 0.8 }}>Jeszcze chwila...</span>}
                              </div>
                            )}
                            <LoadingSpinner isImage />
                          </ProgressContainer>
                        )
                        : (
                            <>
                                <ChatbotMessageDescription isImage>Stwórz opis, aby wygenerować to co chcesz. Przykłady:</ChatbotMessageDescription>
                                <ChatbotMessageDescription isImage isDescription><span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>Lecący smok nad zamkiem, epickie ujęcie, fantasy.</ChatbotMessageDescription>
                                <ChatbotMessageDescription isImage isDescription><span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>Jadący czerwony samochód sportowy przez miasto nocą, światła neonów.</ChatbotMessageDescription>
                                <ChatbotMessageDescription isImage isDescription><span style={{ marginRight: 8, color: "#1e90ff" }}>•</span>Plaża z palmami o zachodzie słońca, fale oceanu, spokojne ujęcie.</ChatbotMessageDescription>
                            </>
                        )}
                    </ChatbotMain>
                    <ChatbotDetailsPanel isDetailsExpanded={isDetailsExpanded} isImage>
                        <ChatbotExpandButton isDetailsExpanded={isDetailsExpanded} onClick={toggleDetailsPanel} isImage>
                            {isDetailsExpanded ? "Zwiń" : "Rozwiń"}
                        </ChatbotExpandButton>
                        <ChatbotMessageDescription isImage>
                            <strong>Jak tworzyć dobre prompty?</strong>
                            <br /><br />
                            Używaj prostych i bezpośrednich opisów. Skup się na głównym obiekcie, jego czynności i otoczeniu. Unikaj skomplikowanych metafor.
                        </ChatbotMessageDescription>
                        <ArtistImage/>
                    </ChatbotDetailsPanel>
                </ChatbotContainer>
            );
        } else {
            returned = handleUnauthorizedAccess();
        }
    } else {
        returned = <LoadingPage />;
    }

    return returned;
};

export default VideoCreatorChatbot;
